import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import { Model } from 'survey-core';
import { Questionnaire } from '../questionnaire';
import { getEntryQuestionnaireSurveyJSON } from '../../view/public/entry-questionnaire/entry-questionnaire-survey-specification';
import {
  entryQuestionnaireReqDto,
  EntryQuestionnaireReqDtoType,
  ErrorCode,
} from '@web/common';
import { QuestionnaireWrapper } from './QuestionnaireWrapper';
import { useParticipantData } from './hooks/useParticipantData';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useParticipantDataStore } from '../../store';
import { RequestErrorType, ServerError } from '../../request';
import { RequestError } from '../../request/RequestError';
import { useNavigateBack } from '../../hooks';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../notification';

export const ViewEntryQuestionnaire = observer(() => {
  const { userInformation } = useParticipantData();
  const participantDataStore = useParticipantDataStore();
  const navigateBack = useNavigateBack();
  const { userId } = useParams();
  const { t } = useTranslation('entry-questionnaire');
  const entryQuestionnaireData = userInformation?.entryQuestionnaire;
  // Staff only correct answers before the participant exists. After that, the study relies on them.
  const readonly =
    userInformation?.id !== userId || userInformation?.Participant !== null;
  // A new json or handler builds a new survey model, which discards the edits.
  const json = useMemo(() => getEntryQuestionnaireSurveyJSON(t), [t]);

  const surveyComplete = useCallback(
    (surveyResult: EntryQuestionnaireReqDtoType, survey: Model) => {
      if (!userId) return;
      participantDataStore
        .updateEntryQuestionnaire(userId, surveyResult)
        .then(() => {
          showSuccessNotification();
          navigateBack();
        })
        .catch((error: unknown) => {
          // Generic errors already show a notification
          if (
            (error instanceof ServerError || error instanceof RequestError) &&
            error.errorCode !== RequestErrorType.Generic
          )
            showErrorNotification(error.errorCode);
          survey.clear(false);
        });
    },
    [userId, participantDataStore, navigateBack],
  );

  const surveyError = useCallback((_: unknown, survey: Model) => {
    showErrorNotification(ErrorCode.QuestionnaireParsingError);
    survey.clear(false);
  }, []);

  return (
    <QuestionnaireWrapper>
      <Questionnaire
        title={'Eintrittsfragebogen'}
        questionnaire={{
          json,
          schema: entryQuestionnaireReqDto,
          initial: entryQuestionnaireData,
        }}
        onComplete={surveyComplete}
        onError={surveyError}
        readonly={readonly}
      />
    </QuestionnaireWrapper>
  );
});
